import React, { createContext, useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import SessionTimeout from '../components/SessionTimeout';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minut
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // 5 minut

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [customClaims, setCustomClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(SESSION_TIMEOUT / 1000);

  // Nasłuchiwanie zmian stanu autentykacji
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Wymuś odświeżenie tokenu, aby pobrać najnowsze custom claims
          const tokenResult = await user.getIdTokenResult(true);
          const claims = tokenResult.claims;
          console.log('Token claims:', claims);

          // Ustaw rolę i custom claims na podstawie tokenu
          if (claims.admin) {
            setUserRole('admin');
          } else if (claims.marketing) {
            setUserRole('marketing');
          } else {
            setUserRole('user');
          }
          setCustomClaims(claims);

          // Pobierz resztę danych użytkownika z Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          // Klonujemy obiekt użytkownika, aby uniknąć bezpośredniej mutacji
          const userWithData = Object.assign(Object.create(Object.getPrototypeOf(user)), user);

          if (userDoc.exists()) {
            // Dodajemy dane z Firestore do obiektu użytkownika
            Object.assign(userWithData, userDoc.data());
            setCurrentUser(userWithData);
          } else {
            const newUserDoc = {
              email: user.email,
              role: 'user',
              displayName: user.displayName || user.email.split('@')[0],
              createdAt: new Date().toISOString(),
            };
            await setDoc(userDocRef, newUserDoc);
            Object.assign(userWithData, newUserDoc);
            setCurrentUser(userWithData);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserRole(null);
          setCurrentUser(user);
        }
      } else {
        console.log('No user logged in');
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);



  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Błąd podczas wylogowywania:', error);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Błąd podczas logowania:', error);
      throw error;
    }
  }, []);

  const handleExtendSession = useCallback(() => {
    // Timer jest teraz resetowany automatycznie przez aktywność
    setShowWarning(false); // Ukryj ostrzeżenie
  }, []);

  const handleCloseWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  // Uruchomienie i resetowanie timera sesji
  useEffect(() => {
    let timeoutId;
    let warningId;
    let intervalId;

    const startTimers = () => {
      clearTimeout(timeoutId);
      clearTimeout(warningId);
      clearInterval(intervalId);

      if (currentUser) {
        setTimeLeft(SESSION_TIMEOUT / 1000);
        setShowWarning(false);

        timeoutId = setTimeout(() => {
          logout();
        }, SESSION_TIMEOUT);

        warningId = setTimeout(() => {
          setShowWarning(true);
          intervalId = setInterval(() => {
            setTimeLeft((prevTime) => {
              if (prevTime <= 1) {
                clearInterval(intervalId);
                logout();
                return 0;
              }
              return prevTime - 1;
            });
          }, 1000);
        }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);
      }
    };

    const resetTimer = () => {
      startTimers();
    };

    if (currentUser) {
      startTimers();
      const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => window.addEventListener(event, resetTimer));

      return () => {
        clearTimeout(timeoutId);
        clearTimeout(warningId);
        clearInterval(intervalId);
        events.forEach(event => window.removeEventListener(event, resetTimer));
      };
    }
  }, [currentUser, logout]);

  const value = {
    currentUser: currentUser,
    userRole,
    login,
    logout,
    loading,
    isAdmin: customClaims?.admin === true,
    isMarketing: customClaims?.marketing === true
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        null // Lub komponent ładowania, jeśli jest potrzebny na całym ekranie
      ) : (
        <>
          <SessionTimeout
            showWarning={showWarning}
            timeLeft={timeLeft}
            onExtend={handleExtendSession}
            onClose={handleCloseWarning}
          />
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthProvider;
